import { IS_NATIVE } from "./flags.js";
import { loadCapacitorCore } from "./plugins.js";

/**
 * Backend API requests.
 *
 * A WebView page is served from `capacitor://localhost` (iOS) / `https://localhost`
 * (Android), which is a *different origin* from feydom.com. The API answers 200 but sends
 * no `Access-Control-Allow-Origin` for those origins, so WebKit discards every response:
 *
 *   Origin capacitor://localhost is not allowed by Access-Control-Allow-Origin
 *
 * The app degrades quietly (browser language, default catalog, no prices), which is why
 * this is invisible until you read the WebView console on a device.
 *
 * CapacitorHttp performs the request natively, outside the WebView, so the same-origin
 * policy never applies — and no backend change is required. It is applied here rather
 * than by enabling the global `CapacitorHttp` plugin flag, because that also patches
 * XMLHttpRequest, which is what GLTFLoader uses for every model and .bin in the catalog.
 */

/**
 * Minimal fetch-shaped view over a native response, so call sites stay unchanged.
 * `statusText` is carried because a dozen callers interpolate it into their error
 * message — without it every native failure reads "Failed to load X: undefined".
 */
function toResponse({ status, data, headers, url }) {
  const ok = status >= 200 && status < 300;
  return {
    ok,
    status,
    statusText: String(status),
    url,
    headers: new Headers(headers ?? {}),
    // CapacitorHttp already parses JSON responses; re-stringify only when it didn't.
    json: async () => (typeof data === "string" ? JSON.parse(data) : data),
    text: async () => (typeof data === "string" ? data : JSON.stringify(data)),
  };
}

/**
 * @param {string} url absolute URL on the API origin
 * @param {{ method?: string, headers?: Record<string,string>, body?: any,
 *           timeout?: number }} [options]
 */
export async function apiFetch(url, options = {}) {
  if (!IS_NATIVE) return fetch(url, options);

  const { CapacitorHttp } = await loadCapacitorCore();
  const headers = options.headers ?? { Accept: "application/json" };
  // CapacitorHttp serializes a JSON body itself; handed fetch's string, it would encode it twice.
  const isJson = Object.entries(headers).some(([k, v]) => /^content-type$/i.test(k) && /json/i.test(v));
  const response = await CapacitorHttp.request({
    url,
    method: options.method ?? "GET",
    headers,
    data: isJson && typeof options.body === "string" ? JSON.parse(options.body) : options.body,
    connectTimeout: options.timeout,
    readTimeout: options.timeout,
  });
  return toResponse({ ...response, url });
}
